/**
 * The widget's intake catalog (D625): WHO the visitor is (an audience) and WHAT it is about (a topic of that audience).
 *
 * An embed offers audiences (`EmbedSettings.audiences`); each audience brings its fixed topic list from this one catalog,
 * the same in every widget. The audience decides the Central queue the conversation lands in (the Service Center realm)
 * and the visitor's participant role; a topic may move a conversation to a narrower realm (a store's question goes to the
 * merchant queue). A topic may name the `@vello/support` category it is, so the server bounds its priority with the help
 * center's own ceiling (a lost item is HIGH) — the visitor never sets a priority.
 *
 * Safety is not a topic: the widget's emergency screen is reachable from anywhere (D427).
 *
 * Neutral: audiences, topics and realms are data keys; labels come from the vocabulary catalog.
 */
import { err, ok } from '@vello/core';
import { refuse } from './reasons.js';
export const WIDGET_AUDIENCES = ['customer', 'driver', 'candidate', 'partner'];
export const WIDGET_TOPICS = [
    // customer
    'ride', 'food', 'send', 'payment', 'lost_item', 'account',
    // driver or courier
    'trip', 'earnings', 'document', 'rider',
    // wants to become a driver or courier
    'signup', 'signup_status',
    // a company or store partner
    'partnership', 'store', 'payout',
];
const AUDIENCES = {
    customer: { realm: 'consumer', role: 'rider', topics: ['ride', 'food', 'send', 'payment', 'lost_item', 'account'] },
    driver: { realm: 'driver', role: 'driver', topics: ['trip', 'earnings', 'document', 'rider', 'account'] },
    candidate: { realm: 'driver', role: 'candidate', topics: ['signup', 'signup_status'] },
    partner: { realm: 'business', role: 'business', topics: ['partnership', 'store', 'payout', 'account'] },
};
/** A topic that answers in another queue than its audience's. */
const TOPIC_QUEUE = {
    store: { realm: 'merchant', role: 'merchant' },
};
const TOPIC_CATEGORY = {
    ride: 'TRIP',
    payment: 'PAYMENT',
    lost_item: 'LOST_ITEM',
    account: 'ACCOUNT',
    trip: 'TRIP',
    earnings: 'PAYMENT',
    document: 'DOCUMENT',
    rider: 'RIDER',
    store: 'MERCHANT',
    payout: 'PAYMENT',
};
/** The topics of an audience, in the widget's order. */
export function topicsOf(audience) {
    return AUDIENCES[audience].topics;
}
export function isWidgetAudience(v) {
    return typeof v === 'string' && WIDGET_AUDIENCES.includes(v);
}
/**
 * The pre-D625 subjects (`ride|food|send|partner`) a widget script still in a browser cache may send: each is now an
 * audience + topic. Kept so an embedded page keeps working across the deploy; new widgets never send it.
 */
export const LEGACY_WIDGET_SUBJECTS = ['ride', 'food', 'send', 'partner'];
const LEGACY_PICK = {
    ride: { audience: 'customer', topic: 'ride' },
    food: { audience: 'customer', topic: 'food' },
    send: { audience: 'customer', topic: 'send' },
    partner: { audience: 'partner', topic: 'partnership' },
};
/** The audience a legacy subject belongs to. */
export function audienceOfLegacySubject(s) {
    return LEGACY_PICK[s].audience;
}
/** The legacy subjects an audience list still answers to (the bootstrap's deprecated `subjects`). */
export function legacySubjectsOf(audiences) {
    return LEGACY_WIDGET_SUBJECTS.filter((s) => audiences.includes(LEGACY_PICK[s].audience));
}
const given = (v) => v !== undefined && v !== null;
/**
 * Resolve the visitor's pick against what the embed offers. The audience must be offered; a topic needs its audience and
 * must be one of that audience's topics. Anything else refuses `WIDGET_SUBJECT_NOT_OFFERED`.
 */
export function resolveIntake(offered, input) {
    let audience = input.audience;
    let topic = input.topic;
    if (!given(audience) && !given(topic) && given(input.subject)) {
        if (!LEGACY_WIDGET_SUBJECTS.includes(input.subject))
            return err(notOffered());
        ({ audience, topic } = LEGACY_PICK[input.subject]);
    }
    if (!given(audience)) {
        if (given(topic))
            return err(notOffered());
        return ok({ realm: AUDIENCES.customer.realm, role: AUDIENCES.customer.role });
    }
    if (!isWidgetAudience(audience) || !offered.includes(audience))
        return err(notOffered());
    const entry = AUDIENCES[audience];
    if (!given(topic))
        return ok({ audience, realm: entry.realm, role: entry.role });
    if (!entry.topics.includes(topic))
        return err(notOffered());
    const t = topic;
    const queue = TOPIC_QUEUE[t] ?? entry;
    const category = TOPIC_CATEGORY[t];
    return ok({ audience, topic: t, realm: queue.realm, role: queue.role, ...(category !== undefined ? { category } : {}) });
}
const notOffered = () => refuse('WIDGET_SUBJECT_NOT_OFFERED', 'this widget does not offer that subject');
//# sourceMappingURL=intake.js.map